import React, { useState, useEffect } from 'react';
import AnnotationData from './AnnotationData';
import {
  FormControl,
  TextField,
  Paper,
  Grid,
  Container,
  Checkbox,
  FormGroup,
  FormLabel,
  FormControlLabel,
} from '@mui/material';
import AnnotationSectionsConfig from '../AnnotationSectionsConfig';
import muiInputStyles from '../../../uiComponents/MuiInput/MuiInputStyles';
import muiCheckboxStyles from '../../../uiComponents/MuiInput/MuiCheckboxStyles';
import './index.css';

const Sources = (props) => {
  const classes = muiInputStyles();
  const checkboxClasses = muiCheckboxStyles();
  const [annoData, setAnnoData] = useState([]);

  const [checkboxMenuItems, setCheckboxMenuItems] = useState({
    DISEASE_DATABASES: {
      benignCheckboxMenuItem: false,
      pathogenicCheckboxMenuItem: false,
      notEvaluatedCheckboxMenuItem: false,
      noVariantInfoCheckboxMenuItem: false,
      evalOptions: [],
    },
    POPULATION_FREQUENCY: {
      benignCheckboxMenuItem: false,
      pathogenicCheckboxMenuItem: false,
      notEvaluatedCheckboxMenuItem: false,
      noVariantInfoCheckboxMenuItem: false,
      evalOptions: [],
    },
    COMPUTATIONAL_PREDICTIVE_PROGRAMS: {
      benignCheckboxMenuItem: false,
      pathogenicCheckboxMenuItem: false,
      notEvaluatedCheckboxMenuItem: false,
      noVariantInfoCheckboxMenuItem: false,
      evalOptions: [],
    },
    NUCLEOTIDE_CONSERVATION: {
      benignCheckboxMenuItem: false,
      pathogenicCheckboxMenuItem: false,
      notEvaluatedCheckboxMenuItem: false,
      noVariantInfoCheckboxMenuItem: false,
      evalOptions: [],
    },
  });

  useEffect(() => {
    const { sourceType, annoData } = props;
    setAnnoData(annoData);
    disableMenuItems(props.categoryValues[props.categoryType].eval);
  }, [props]);

  const onEvaluationCheckboxChange = (event, source) => {
    const checkboxName = event.target.name;
    const isChecked = event.target.checked;

    if (isChecked) {
        checkboxMenuItems[props.categoryType].evalOptions.push(checkboxName);
        setCheckboxMenuItems({...checkboxMenuItems});
    } else {
        checkboxMenuItems[props.categoryType].evalOptions = checkboxMenuItems[props.categoryType].evalOptions.filter((item) => item !== checkboxName);
        setCheckboxMenuItems({...checkboxMenuItems});
    }
    
    // Disable the checkbox menu items based on the prefix selected
    let selectedOptions = checkboxMenuItems[props.categoryType].evalOptions;
    updateMenuItemOptions(selectedOptions[selectedOptions.length - 1], selectedOptions);
    
  };

  const onTextChange = (event, source) => {
    props.onTextChange(source, event.target.value);
  };

  // Helper function to determine the state of menu items based on their prefix or name
  const getMenuItemStates = (menuItem) => {
    const prefix = menuItem.charAt(0);
    return {
      isBenign: prefix === "B", // Menu item starts with "B" (Benign)
      isPathogenic: prefix === "P", // Menu item starts with "P" (Pathogenic)
      isNotEvaluated: menuItem === "Not Evaluated", // Menu item is "Not Evaluated"
      isNoVariantInfo: menuItem === "No Information on Variant", // Menu item is "No Information on Variant"
    };
  };

  // Updates the state of menu items based on the previously selected menu item
  const updateMenuItemOptions = (prevSelectedMenuItem, selectedOptions) => {

    // Get prefixes for the options selected, create edge case conditons
    let prefixArr = selectedOptions.map(item => item[0]);
    let condition1 = ((selectedOptions.includes("Not Evaluated") || selectedOptions.includes("No Information on Variant")) && selectedOptions.length > 1);
    let condition2 = (prefixArr.includes('B') && prefixArr.includes('P'));

    if (!prevSelectedMenuItem || condition1 || condition2) {
      resetCheckboxStateItems();
      props.onEvaluationChange(props.categoryType, selectedOptions);
      return;
    }

    // Get the states of the previous menu item
    const { isBenign, isPathogenic, isNotEvaluated, isNoVariantInfo } = getMenuItemStates(prevSelectedMenuItem);

    // Update the states of menu items based on the previous selection
    checkboxMenuItems[props.categoryType].evalOptions = selectedOptions;
    checkboxMenuItems[props.categoryType].benignCheckboxMenuItem = (isPathogenic || isNotEvaluated || isNoVariantInfo);    
    checkboxMenuItems[props.categoryType].pathogenicCheckboxMenuItem = (isBenign || isNotEvaluated || isNoVariantInfo);    
    checkboxMenuItems[props.categoryType].noVariantInfoCheckboxMenuItem = (isBenign || isPathogenic || isNotEvaluated);    
    checkboxMenuItems[props.categoryType].notEvaluatedCheckboxMenuItem = (isBenign || isPathogenic || isNoVariantInfo);    
    setCheckboxMenuItems({...checkboxMenuItems});
    props.onEvaluationChange(props.categoryType, selectedOptions);
  };

  //Disables menu items based on the currently selected options
  const disableMenuItems = (selectedOptions) => {

    // Prevent the function from running if selectedOptions is null, undefined, or empty
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    // Get prefixes for the options selected, create edge case conditons
    let prefixArr = selectedOptions.map(item => item[0]);
    let condition1 = ((selectedOptions.includes("Not Evaluated") || selectedOptions.includes("No Information on Variant")) && selectedOptions.length > 1);
    let condition2 = (prefixArr.includes('B') && prefixArr.includes('P'));

    // Reset all items to enabled if it doesnt meet these conditions
    if (condition1 || condition2) {
      resetCheckboxStateItems();  
      return;
    }

    // Reset all menu item states before applying new rules
    resetCheckboxStateItems();
    checkboxMenuItems[props.categoryType].evalOptions = selectedOptions;

    // Iterate through the selected options and update menu item states
    selectedOptions.forEach((element) => {
      const { isBenign, isPathogenic, isNotEvaluated, isNoVariantInfo } = getMenuItemStates(element);

      if (isBenign) {
        // If "Benign" is selected, disable conflicting options
        checkboxMenuItems[props.categoryType].pathogenicCheckboxMenuItem = true;
        checkboxMenuItems[props.categoryType].notEvaluatedCheckboxMenuItem = true;
        checkboxMenuItems[props.categoryType].noVariantInfoCheckboxMenuItem = true;
      } else if (isPathogenic) {
        // If "Pathogenic" is selected, disable conflicting options
        checkboxMenuItems[props.categoryType].benignCheckboxMenuItem = true;
        checkboxMenuItems[props.categoryType].notEvaluatedCheckboxMenuItem = true;
        checkboxMenuItems[props.categoryType].noVariantInfoCheckboxMenuItem = true;
      } else if (isNotEvaluated) {
        // If "Not Evaluated" is selected, disable conflicting options
        checkboxMenuItems[props.categoryType].benignCheckboxMenuItem = true;
        checkboxMenuItems[props.categoryType].pathogenicCheckboxMenuItem = true;
        checkboxMenuItems[props.categoryType].noVariantInfoCheckboxMenuItem = true;
      } else if (isNoVariantInfo) {
        // If "No Information on Variant" is selected, disable conflicting options
        checkboxMenuItems[props.categoryType].benignCheckboxMenuItem = true;
        checkboxMenuItems[props.categoryType].pathogenicCheckboxMenuItem = true;
        checkboxMenuItems[props.categoryType].notEvaluatedCheckboxMenuItem = true;
      }
    });

    setCheckboxMenuItems({...checkboxMenuItems});
  };

  const resetCheckboxStateItems = () => {
    checkboxMenuItems[props.categoryType].evalOptions = [];
    checkboxMenuItems[props.categoryType].benignCheckboxMenuItem = false;    
    checkboxMenuItems[props.categoryType].pathogenicCheckboxMenuItem = false;    
    checkboxMenuItems[props.categoryType].noVariantInfoCheckboxMenuItem = false;    
    checkboxMenuItems[props.categoryType].notEvaluatedCheckboxMenuItem = false;    
    setCheckboxMenuItems({...checkboxMenuItems});
  }

  // Renders the evaluation dropdown and comments input section for the given category
  const renderEvalComments = (category) => {
    return <div className="form-item">
      <FormControl fullWidth>
        <TextField
          id="comments"
          fullWidth
          size="small"
          name="comments"
          className={classes.muiInputs}
          value={props.categoryValues[category].comments || ""}
          onChange={(e) => onTextChange(e, category)}
          variant="outlined"
          label="Comments"
          aria-label="Comments"
          multiline
          minRows={4}
          inputProps={{ maxLength: 1000 }}
        />
      </FormControl>
    </div>;
  };

  const renderCheckboxItems = () => {
    let checkboxItems = [];
    AnnotationSectionsConfig[props.sourceType].categories.map((category) => {
    let criteriaPrefix = category.name.charAt(0);
    let disabled = true;

    if (criteriaPrefix === "B") 
      disabled=checkboxMenuItems[props.categoryType].benignCheckboxMenuItem;
    else if (criteriaPrefix === "P")
      disabled= checkboxMenuItems[props.categoryType].pathogenicCheckboxMenuItem;
    else if (category.name === "Not Evaluated")
      disabled = checkboxMenuItems[props.categoryType].notEvaluatedCheckboxMenuItem;
    else  if (category.name === "No Information on Variant")
      disabled = checkboxMenuItems[props.categoryType].noVariantInfoCheckboxMenuItem;

    checkboxItems.push(
        <FormControlLabel
          key={category.name}
          classes={{ label: checkboxClasses.checkboxLabel }}
          control={
            <Checkbox
              className={checkboxClasses.checkboxRoot}
              name={category.name}
              classes={{
                root: checkboxMenuItems[props.categoryType].evalOptions.includes(category.name)
                  ? checkboxClasses.checkboxSelected
                  : checkboxClasses.checkboxRoot
              }}
              disabled={disabled}
              checked={checkboxMenuItems[props.categoryType].evalOptions.includes(category.name)}
              onChange={onEvaluationCheckboxChange}
            />
          }
          label={category.name}
        />
      );
    } );
    
    return (
      <div className="form-item">
        <FormGroup>
          <FormLabel component="legend" className={checkboxClasses.formLabel}>Evaluation</FormLabel>
          <FormControl variant="outlined" >
              {checkboxItems}
          </FormControl>
        </FormGroup>
      </div>
    )
  };

  const renderAnnotationData = (category, categoryAnno, fieldDescription) => {
    return (
      <>
        <div className="form-item">
          <AnnotationData
            source={category}
            annotationData={categoryAnno}
            fieldDescription={fieldDescription}
            idPrefix={props.categoryType}
          />
        </div>
      </>
    );
  };

  const renderCategoryData = (category, categoryAnno, fieldDescription) => {
    return (
      <>
        <Grid container spacing={3} className="dashGridContainer">
          <Grid item xs={12} md={8} lg={9}>
            {renderAnnotationData(category, categoryAnno, fieldDescription)}
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            {renderCheckboxItems()}
            {renderEvalComments(category)}
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <>
      {props.annoData.length === 0 ? (
        <Container maxWidth="xl">
          <Paper className="card cardContainer" elevation={0}>
            <Grid container spacing={3} className="dashGridContainer">
              <Grid item xs={12} md={8} lg={9}>
                <div className="form-item">
                  There is no data available for this section
                </div>
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                {renderCheckboxItems()}
                {renderEvalComments(props.categoryType)}
              </Grid>
            </Grid>
          </Paper>
        </Container>
      ) : (
        <Container key={props.categoryType} maxWidth="xl">
          <Paper className="card cardContainer" elevation={0}>
            {renderCategoryData(props.categoryType, props.annoData, props.fieldDescription)}
          </Paper>
        </Container>
      )}
    </>
  );
};

export default Sources;